import { Col, Row } from "react-bootstrap";
import {
  getReceiptPrintDate,
  getDurationString,
  formatCurrency,
  parseToThousandSeperatorWholeNumber,
} from "../../../../shared/utility";
import { TableCustomHeader } from "../../../../shared/components";
import KpiTable from "../../../../shared/components/table/KpiTable";
import { getBreakdownTotalLabel } from "../../../../shared/utilities/columnHeadersUtility/stockAudit";

// Main Container
const AuditSummary = ({ children, isPrintView = false }) => {
  return (
    <Row className={`m-0 border-bottom ${isPrintView ? "pb-3" : "pb-4"}`}>
      {children}
    </Row>
  );
};

// Basic Info Section
AuditSummary.BasicInfo = ({ stockAudit, isPrintView = false, children }) => {
  if (isPrintView) {
    return (
      <Col md={3}>
        <Row className="mb-2 d-flex align-items-center">
          <Col md={5}>
            <span className="predefinded-audit-label">Nickname:</span>
          </Col>
          <Col md={7}>
            <span>{stockAudit?.nickName || ""}</span>
          </Col>
        </Row>

        <Row className="mb-2 d-flex align-items-center">
          <Col md={5}>
            <span className="predefinded-audit-label">Start Date:</span>
          </Col>
          <Col md={7}>
            <span>{getReceiptPrintDate(stockAudit?.startDate, true)}</span>
          </Col>
        </Row>

        <Row className="mb-2 d-flex align-items-center">
          <Col md={5}>
            <span className="predefinded-audit-label">End Date:</span>
          </Col>
          <Col md={7}>
            <span>{getReceiptPrintDate(stockAudit?.endDate, true)}</span>
          </Col>
        </Row>

        <Row className="mb-2 d-flex align-items-center">
          <Col md={5}>
            <span className="predefinded-audit-label">Duration:</span>
          </Col>
          <Col md={7}>
            <span>
              {getDurationString(stockAudit?.startDate, stockAudit?.endDate) ||
                ""}
            </span>
          </Col>
        </Row>
        <Row className="mb-2 d-flex align-items-center">
          <Col md={5}>
            <span className="predefinded-audit-label">Notes:</span>
          </Col>
          <Col md={7}>
            <span>{stockAudit?.notes || ""}</span>
          </Col>
        </Row>
      </Col>
    );
  }

  return (
    <Col md={3}>
      {children}

      <Row className="mb-3 d-flex align-items-center">
        <Col md={3}>
          <span className="predefinded-audit-label">Start Date:</span>
        </Col>
        <Col md={3}>
          <span>{getReceiptPrintDate(stockAudit?.startDate, true)}</span>
        </Col>
        <Col md={3}>
          <span className="predefinded-audit-label">End Date:</span>
        </Col>
        <Col md={3}>
          <span>{getReceiptPrintDate(stockAudit?.endDate, true)}</span>
        </Col>
      </Row>

      <Row className="mb-3 d-flex align-items-center">
        <Col md={3}>
          <span className="predefinded-audit-label">Start By:</span>
        </Col>
        <Col md={3}>
          <span>{stockAudit?.startedBy || ""}</span>
        </Col>
        <Col md={3}>
          <span className="predefinded-audit-label">Duration:</span>
        </Col>
        <Col md={3}>
          <span>
            {getDurationString(stockAudit?.startDate, stockAudit?.endDate) ||
              ""}
          </span>
        </Col>
      </Row>
    </Col>
  );
};

// Statistics Section
AuditSummary.Statistics = ({
  auditBreakdownValue,
  stockAuditBreakdown,
  stockAuditStatistics,
  isPrintView = false,
}) => {
  if (isPrintView) {
    return (
      <Col md={3}>
        <Row className="mb-2">
          <Col md={7} className="d-flex align-items-center">
            <span className="predefinded-audit-label">
              {getBreakdownTotalLabel(auditBreakdownValue)}
            </span>
          </Col>
          <Col md={5}>
            <span>{stockAuditBreakdown?.totalRecords ?? 0}</span>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={7} className="d-flex align-items-center">
            <span className="predefinded-audit-label">Largest Qty Diff:</span>
          </Col>
          <Col md={5}>
            <span
              className={`${
                stockAuditStatistics?.largestQtyDiff > 0
                  ? "text-green"
                  : stockAuditStatistics?.largestQtyDiff < 0
                  ? "text-danger"
                  : ""
              }`}
            >
              {parseToThousandSeperatorWholeNumber(
                stockAuditStatistics?.largestQtyDiff
              )}
            </span>
          </Col>
        </Row>

        <Row>
          <Col md={7} className="d-flex align-items-center">
            <span className="predefinded-audit-label">Largest Price Diff:</span>
          </Col>
          <Col md={5}>
            <span
              className={`${
                stockAuditStatistics?.largestPriceDiff > 0
                  ? "text-green"
                  : stockAuditStatistics?.largestPriceDiff < 0
                  ? "text-danger"
                  : ""
              }`}
            >
              {formatCurrency(stockAuditStatistics?.largestPriceDiff)}
            </span>
          </Col>
        </Row>
      </Col>
    );
  }

  return (
    <Col md={2}>
      <Row className="mt-3">
        <Col md={6} className="mb-4 d-flex align-items-center">
          <span className="predefinded-audit-label">
            {getBreakdownTotalLabel(auditBreakdownValue)}
          </span>
        </Col>
        <Col md={6}>
          <span>{stockAuditBreakdown?.totalRecords ?? 0}</span>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-4 d-flex align-items-center">
          <span className="predefinded-audit-label">Largest Qty Diff:</span>
        </Col>
        <Col md={6} className="d-flex gap-2 flex-wrap">
          <TableCustomHeader
            label={
              <span>
                {stockAuditStatistics?.largestQtyDiffProduct?.length > 10
                  ? `${stockAuditStatistics?.largestQtyDiffProduct?.slice(
                      0,
                      10
                    )}...`
                  : stockAuditStatistics?.largestQtyDiffProduct}
              </span>
            }
            tooltopText={
              <span>{stockAuditStatistics?.largestQtyDiffProduct}</span>
            }
            id={`tooltip-qty-${stockAuditStatistics?.largestQtyDiffProduct
              ?.replace(/[^a-zA-Z0-9]+/g, "-")
              .toLowerCase()}`}
            className="sku-tooltip"
          />
          <span
            className={`${
              stockAuditStatistics?.largestQtyDiff > 0
                ? "text-green"
                : stockAuditStatistics?.largestQtyDiff < 0
                ? "text-danger"
                : ""
            }`}
          >
            {parseToThousandSeperatorWholeNumber(
              stockAuditStatistics?.largestQtyDiff
            )}
          </span>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-4 d-flex align-items-center">
          <span className="predefinded-audit-label">Largest Price Diff:</span>
        </Col>
        <Col md={6} className="d-flex gap-2 flex-wrap">
          <TableCustomHeader
            label={
              <span>
                {stockAuditStatistics?.largestPriceDiffProduct?.length > 10
                  ? `${stockAuditStatistics?.largestPriceDiffProduct?.slice(
                      0,
                      10
                    )}...`
                  : stockAuditStatistics?.largestPriceDiffProduct}
              </span>
            }
            tooltopText={
              <span>{stockAuditStatistics?.largestPriceDiffProduct}</span>
            }
            id={`tooltip-price-${stockAuditStatistics?.largestPriceDiffProduct
              ?.replace(/[^a-zA-Z0-9]+/g, "-")
              .toLowerCase()}`}
            className="sku-tooltip"
          />
          <span
            className={`${
              stockAuditStatistics?.largestPriceDiff > 0
                ? "text-green"
                : stockAuditStatistics?.largestPriceDiff < 0
                ? "text-danger"
                : ""
            }`}
          >
            {formatCurrency(stockAuditStatistics?.largestPriceDiff)}
          </span>
        </Col>
      </Row>
    </Col>
  );
};

// KPI Section
AuditSummary.Kpi = ({ expandedData, isPrintView = false }) => {
  return (
    <Col md={isPrintView ? 6 : 5}>
      <KpiTable data={expandedData} />
    </Col>
  );
};

// Notes Section (only for display)
AuditSummary.Notes = ({ children }) => {
  return <Col md={2}>{children}</Col>;
};

export default AuditSummary;
